// src/pages/LikedProdcut.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useBuyer } from '../contexts/BuyerContext';
import { useProductState, useProductActions } from '../contexts/ProductContext';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import ProductDrawer from '../components/ProductDrawer';
import { mergeProductsWithSpecs, normalizeString } from '../components/catalogUtils';
import { CatalogSkeleton, DrawerSkeleton } from '../components/Skeleton';
import { useNavigate } from 'react-router-dom';
import '../components/Catalog.css';
import './LikedProducts.css';

const LikedProducts = () => {
  const { buyer } = useBuyer();
  const { likedProducts: likedProductIds, selectedProducts } = useProductState();
  const { toggleLike, addToSelected } = useProductActions();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [sizes, setSizes] = useState([{ size: 'Regular', quantity: 1 }]);

  // Fetch full product data for each liked productId
  useEffect(() => {
    const fetchProducts = async () => {
      if (!buyer?.buyerId || likedProductIds.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const productPromises = likedProductIds.map(async (productId) => {
          const docRef = doc(db, 'products', productId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
          }
          return null; // product may have been deleted
        });

        const results = await Promise.all(productPromises);
        const validProducts = results.filter(Boolean);
        setProducts(validProducts);
      } catch (err) {
        console.error('Error fetching liked products:', err);
        setError('Failed to load product details.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [buyer?.buyerId, likedProductIds]);

  // On-demand spec fetch per clicked product (same pattern as Catalog & ProductDetails)
  const fetchSpecForProduct = useCallback(async (product) => {
    if (!product?.productName) return null;
    try {
      const q = query(
        collection(db, 'productSpecs'),
        where('segment', '==', product.segment),
        where('category', '==', product.category),
        where('productName', '==', product.productName)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const allSpecs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const productSegment = normalizeString(product.segment || '').toLowerCase();
      const isGemsOrPearls = productSegment === 'gems' || productSegment === 'pearls';
      const isRudraksha = productSegment === 'rudraksha' || productSegment === 'rudarkh' ||
        normalizeString(product.productName || '').toLowerCase() === 'rudraksha' ||
        normalizeString(product.productName || '').toLowerCase().includes('rudraksha');
      const isDecor = productSegment === 'decor';
      const isDiamondOrPlatinum = productSegment === 'diamond' || productSegment === 'platinum';

      const matchedSpec = allSpecs.find(
        spec =>
          spec.segment === product.segment &&
          spec.category === product.category &&
          spec.productName === product.productName &&
          spec.productSource?.toLowerCase().trim() ===
            (product.productSource || '').toLowerCase().trim() &&
          spec.finishType?.toLowerCase().trim() ===
            (product.styleType || product.finishType || '').toLowerCase().trim() &&
          // For Rudraksha: match on segment, category, productName, productSource, finishType only
          // Skip color/spec/shape matching entirely
          // For Gems/Pearls: match on color AND shape (instead of specification)
          // For Decor: match on color (instead of specification)
          // For Diamond/Platinum: specification is embedded in the product document itself
          // For all other segments (Brand, Tool, Machine, Gold, Silver, etc.): match on specification
          (isRudraksha
            ? true
            : (isGemsOrPearls
                ? spec.color && product.color &&
                  normalizeString(spec.color) === normalizeString(product.color) &&
                  spec.shape && product.shape &&
                  normalizeString(spec.shape) === normalizeString(product.shape)
                : isDecor
                    ? spec.color && product.color &&
                      normalizeString(spec.color) === normalizeString(product.color)
                    : isDiamondOrPlatinum
                        ? true
                        : spec.specification && product.specification &&
                          normalizeString(spec.specification) === normalizeString(product.specification))
          )
      );
      return matchedSpec || null;
    } catch (err) {
      console.error('❌ Error fetching spec for product:', err);
      return null;
    }
  }, []);

  const handleRemoveLike = (e, productId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (window.confirm('Are you sure you want to remove this product from your favorites?')) {
      toggleLike(productId);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const handleSelectedClick = useCallback(async (product) => {
    setSizes([{ size: 'Regular', quantity: 1 }]);
    setDrawerLoading(true);
    setSelectedProduct(null);
    setDrawerOpen(true);

    const spec = await fetchSpecForProduct(product);

    const merged = spec
      ? mergeProductsWithSpecs([product], [spec])[0]
      : { ...product, hasSpecification: false, specId: null };

    setSelectedProduct(merged);
    setDrawerLoading(false);
  }, [fetchSpecForProduct]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
    setDrawerLoading(false);
    setSizes([{ size: 'Regular', quantity: 1 }]);
  };

  const handleAddToSelection = (productWithVariants, serviceType) => {
    if (!productWithVariants) return;
    if (!productWithVariants.hasSpecification) {
      alert('Product specifications are not available. Cannot add to order list.');
      return;
    }
    const finalProduct = {
      ...productWithVariants,
      serviceType: serviceType || 'ready',
    };
    addToSelected(finalProduct);
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <>
        <Header title="Favourites" showBack={true} />
        <CatalogSkeleton count={4} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header title="Favourites" showBack={true} />
        <div className="liked-products-container">
          <p className="error-message">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Favourites" showBack={true} />
      <div className="liked-products-container">
        {products.length === 0 ? (
          <div className="empty-state">
            <p>❤️ You haven't liked any products yet.</p>
            <button className="browse-btn" onClick={() => navigate('/productSegment/GOLD')}>
              Browse Catalog
            </button>
          </div>
        ) : (
          <div className="catalog-grid">
            {products.map((product) => {
              const isSelected = selectedProducts.some((p) => p.id === product.id);
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  isLiked={true}
                  isSelected={isSelected}
                  onProductClick={handleProductClick}
                  onLikeClick={(e) => handleRemoveLike(product.id, e)}
                  onSelectedClick={() => handleSelectedClick(product)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Drawer: show loading skeleton while spec is being fetched */}
      {drawerOpen && (
        drawerLoading || !selectedProduct ? (
          <div className="bottom-drawer-overlay" onClick={closeDrawer}>
            <div className="bottom-drawer" onClick={(e) => e.stopPropagation()}>
              <button
                className="close-btn"
                onClick={closeDrawer}
                aria-label="Close"
                style={{ position: 'absolute', top: 12, right: 12 }}
              >
                ×
              </button>
              <DrawerSkeleton />
            </div>
          </div>
        ) : (
          <ProductDrawer
            isOpen={drawerOpen}
            product={selectedProduct}
            sizes={sizes}
            serviceType="ready"
            onClose={closeDrawer}
            onSizeChange={setSizes}
            onAddToSelection={handleAddToSelection}
          />
        )
      )}
    </>
  );
};

export default LikedProducts;
